<template>
  <div class="register-rol-view">
    <div
      v-if="isLoading"
      class="loading"
    >
      <unc-loader />
    </div>

    <div
      v-else
      class="register-card"
    >
      <div class="register-glow" />

      <div class="card-header ch-alt register-header">
        <div class="register-kicker rr-kicker--info">
          Paso 2 de 4
        </div>
        <h1
          id="rol-title"
          class="register-title"
        >
          Seleccioná tu rol
        </h1>
        <p class="register-subtitle">
          Indicanos cuál es tu rol más representativo dentro de la UNC.
        </p>
      </div>

      <div class="card-body register-body">
        <div
          class="rr-person-list"
          role="radiogroup"
          aria-labelledby="rol-title"
        >
          <label
            v-for="rol of orderedRoles"
            :key="rol.code"
            class="rr-person-card"
            :class="{ 'is-selected': selectedPersonRol && selectedPersonRol.code === rol.code }"
          >
            <input
              v-model="selectedPersonRol"
              type="radio"
              name="userName"
              :value="rol"
              class="rr-person-radio"
            >
            <div class="rol-icon">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.8"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div class="rr-person-info">
              <span class="rr-person-name">{{ rol.name }}</span>
            </div>
            <div class="rr-person-check">
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2.5"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </label>
        </div>

        <div class="rr-actions">
          <button
            type="button"
            class="btn btn-link waves-effect register-btn register-btn-secondary"
            @click="goBack()"
          >
            Volver
          </button>
          <button
            type="button"
            class="btn btn-primary waves-effect register-btn register-btn-primary"
            @click="goNext()"
          >
            <span>Siguiente</span>
            <svg
              aria-hidden="true"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { swal } from '../../share.js';
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'RegisterPersonRol',
    data () {
        return {
            isLoading: true,
            roles: [],
            selectedPersonRol: '',
        };
    },
    computed: {
        orderedRoles: function () {
            return [...this.roles].sort((a, b) => (a.code > b.code) - (a.code < b.code));
        },
        ...mapGetters([
            'sharedComponentsInfo',
            'users',
        ]),
    },
    async mounted () {
        await this.getData();
    },
    methods: {
        ...mapActions([
            'getRoles',
        ]),
        async getData () {
            this.isLoading = true;
            try {
                const res = await this.getRoles();
                this.roles = res;

                if (this.sharedComponentsInfo.selectedPersonRol) {
                    this.selectedPersonRol = this.sharedComponentsInfo.selectedPersonRol;
                }
            } catch (error) {
                swal({
                    title: 'Algo salió mal',
                    text: 'No pudimos cargar los roles. Por favor, intentá nuevamente más tarde.',
                    type: 'warning',
                });
                this.$store.commit('setCurrentComponent', 'register-search');
            } finally {
                this.isLoading = false;
            }
        },
        goNext () {
            this.$store.commit('setSelectedPersonRol', this.selectedPersonRol);
            if (this.selectedPersonRol === '') {
                swal({
                    title: 'Debés seleccionar un rol',
                    text: 'Seleccioná el rol que más te represente para continuar con el registro.',
                    type: 'warning',
                });
                return;
            }
            this.$store.commit('setCurrentComponent', 'register-person-form');
        },
        goBack () {
            this.$store.commit('setSelectedPersonRol', this.selectedPersonRol);

            if (this.users.length) {
                this.$store.commit('setCurrentComponent', 'register-search-results');
            } else {
                this.$store.commit('setCurrentComponent', 'register-search');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import './register-shared.scss';

.register-rol-view {
  --register-azul: #003865;
  --register-azul-deep: #001f3f;
  --register-azul-mid: #0a5f96;
  --register-verde: #3eb6b8;
  --register-ink: #102238;
  --register-muted: #526579;

  position: relative;
  padding: 0;
}

.rr-person-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.rr-person-radio {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

.rr-person-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 10px;
  border-radius: 14px;
  border: 1.5px solid rgba(0, 56, 101, 0.12);
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.22s ease, box-shadow 0.22s ease, background-color 0.22s ease, transform 0.22s ease;
  position: relative;
  overflow: hidden;
}

.rr-person-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(to bottom, var(--register-azul), var(--register-verde));
  transform: scaleY(0);
  transform-origin: bottom;
  transition: transform 0.25s ease;
  border-radius: 0 2px 2px 0;
}

.rr-person-card:hover {
  border-color: rgba(62, 182, 184, 0.4);
  box-shadow: 0 4px 16px rgba(0, 56, 101, 0.1);
  transform: translateY(-1px);
}

.rr-person-card.is-selected {
  border-color: rgba(62, 182, 184, 0.7);
  background: linear-gradient(135deg, rgba(62, 182, 184, 0.06), rgba(0, 56, 101, 0.04));
  box-shadow: 0 8px 24px rgba(62, 182, 184, 0.18);
}

.rr-person-card.is-selected::before {
  transform: scaleY(1);
}

.rol-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--register-azul), var(--register-azul-mid));
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.22s ease, color 0.22s ease;
}

.rol-icon svg {
  width: 22px;
  height: 22px;
  color: currentColor;
  stroke: currentColor;
}

.rr-person-card.is-selected .rol-icon {
  background: linear-gradient(135deg, var(--register-azul-mid), var(--register-verde));
  color: white;
}

.rr-person-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.rr-person-name {
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 600;
  color: var(--register-ink);
}

.rr-person-check {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid rgba(0, 56, 101, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.22s ease;
  background: transparent;
}

.rr-person-check svg {
  width: 13px;
  height: 13px;
  color: transparent;
  stroke: currentColor;
  transition: color 0.2s ease;
}

.rr-person-card.is-selected .rr-person-check {
  background: var(--register-verde);
  border-color: var(--register-verde);
}

.rr-person-card.is-selected .rr-person-check svg {
  color: white;
}

.rr-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.register-rol-view .register-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 991px) {
  .rr-actions {
    justify-content: stretch;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .rr-actions .register-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .rr-person-card {
    padding: 14px;
    gap: 12px;
  }

  .rol-icon {
    width: 36px;
    height: 36px;
  }

  .rol-icon svg {
    width: 18px;
    height: 18px;
  }
}
</style>
